import { Menu } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { Brand } from '../../components/common/Brand'
import { NotificationPanel } from '../../features/notifications/NotificationPanel'
import { initials } from '../../lib/utils'
import { useAuth } from '../providers/auth-context'
import type { NavigationItem, NavigationSection } from './navigation'

function findCurrentItem(pathname: string, sections: NavigationSection[]) {
  return sections
    .flatMap((section) => section.items)
    .filter((item): item is NavigationItem & { to: string } => Boolean(item.to))
    .filter((item) => pathname === item.to || (!item.end && pathname.startsWith(`${item.to}/`)))
    .sort((left, right) => right.to.length - left.to.length)[0]
}

function roleLabel(admin: boolean, roles: string[], batchYear: number | null) {
  if (roles.includes('superadmin')) return 'Superadmin'
  if (roles.includes('operations_admin')) return 'Pentadbir Alumni'
  if (admin) return batchYear ? `Pentadbir Batch ${batchYear}` : 'Pentadbir'
  return 'Alumni'
}

export function AppHeader({
  admin,
  mobileOpen,
  sections,
  onOpenMobile,
}: {
  admin: boolean
  mobileOpen: boolean
  sections: NavigationSection[]
  onOpenMobile: () => void
}) {
  const { pathname } = useLocation()
  const { access, session } = useAuth()
  const currentItem = findCurrentItem(pathname, sections)
  const contextLabel = admin ? 'Pentadbiran' : 'Ruang alumni'
  const displayName = access?.displayName ?? (admin ? 'Pentadbiran' : 'Alumni')
  const canUseAlumniNotifications = Boolean(
    access?.profileId &&
      access.accountStatus === 'active' &&
      access.applicationStatus === 'approved' &&
      access.roles.includes('alumni'),
  )

  return (
    <header className="app-topbar">
      <button
        id="app-menu-trigger"
        className="icon-button menu-button"
        type="button"
        aria-label="Buka navigasi"
        aria-controls="app-sidebar"
        aria-expanded={mobileOpen}
        onClick={onOpenMobile}
      >
        <Menu aria-hidden="true" />
      </button>

      <span className="app-topbar__brand" aria-hidden="true">
        <Brand variant="symbol" to={admin ? '/admin' : '/app'} ariaHidden />
      </span>

      <nav className="app-breadcrumb" aria-label="Breadcrumb">
        <span>{contextLabel}</span>
        <span aria-hidden="true">/</span>
        <strong>{currentItem?.label ?? contextLabel}</strong>
      </nav>

      <div className="app-topbar__identity">
        {session && access?.userId && canUseAlumniNotifications ? (
          <NotificationPanel userId={access.userId} />
        ) : null}
        <div className="app-user-summary">
          <span className="app-user-summary__avatar" aria-hidden="true">
            {initials(displayName) || 'SA'}
          </span>
          <span className="app-user-summary__copy">
            <strong>{displayName}</strong>
            <small>{roleLabel(admin, access?.roles ?? [], access?.adminBatchYear ?? null)}</small>
          </span>
        </div>
      </div>
    </header>
  )
}
